import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ExternalLink, Palette, PanelBottom, Receipt, Image as ImageIcon, Settings, Upload, Trash2, RefreshCw, FileJson, Copy, Check, Plug, Box, Eye, EyeOff, CheckCircle, AlertTriangle, Gamepad2, ClipboardList, Home, Sparkles, Navigation as NavigationIcon, Compass } from "lucide-react";
import TenantPreferencesService, { AppPropertiesResult, BrandingConfig, BrandingSlot, ScormConfig, LcmConfig, ATTENDANCE_COLUMN_KEYS, AttendanceColumnKey } from "../../_services/TenantPreferencesService";
import ScormService from "../../_services/scorm.service";
import StartPagePicker from "./StartPagePicker";
import SystemTab from "./SystemTab";
import ThemeTab from "./ThemeTab";
import FooterTab from "./FooterTab";
import NavigationTab from "./NavigationTab";
import AtlasTab from "./AtlasTab";
import DocumentTemplateService from "../../_services/DocumentTemplateService";
import "./tenant-preferences.css";

type TabKey = "branding" | "ilearn-props" | "start-page" | "ilearn-features" | "receipt" | "system" | "theme" | "footer" | "navigation" | "scorm" | "ai-providers" | "lcm" | "atlas";

// Order mirrors the sidebar's grouped layout: Appearance, Student App, Operations.
const TAB_KEYS: TabKey[] = ["branding", "theme", "start-page", "footer", "navigation", "ilearn-features", "ilearn-props", "atlas", "system", "receipt", "lcm", "scorm"];

// iLearn feature flags editable from this page. Stored in
// customer_detail_master.iLearnAppConfig.features (default-on in ilearn: a
// feature shows unless its key is explicitly false). Add new toggles here.
const ILEARN_FEATURE_DEFS: { key: string; label: string; hint: string }[] = [
  {
    key: "games",
    label: "Games Zone",
    hint: "Quiz battles, coin wallet and leaderboard. Shows a Games Zone entry on the student dashboard, Student Hub and Control Panel sidebar.",
  },
];

// Student Hub hero-button flags (iLearnAppConfig.studentHub.heroButtons).
// Same default-on semantics. Keys are historical — letsPlay renders as
// "Practice Zone" in ilearn but keeps its original config key.
const HERO_BUTTON_DEFS: { key: string; label: string; hint: string }[] = [
  { key: "viewTimetable", label: "View Timetable", hint: "Primary timetable button in the Student Hub hero." },
  { key: "takeATest", label: "Take a Test", hint: "Shortcut to the test list." },
  { key: "assignments", label: "Assignments", hint: "Shortcut to /assignments." },
  { key: "letsPlay", label: "Practice Zone", hint: "Tag-based question practice playground (/play-ground). Config key: letsPlay." },
  { key: "learnFromTutorials", label: "Learn from Tutorials", hint: "Shortcut to /tutorials." },
  { key: "myCertificates", label: "My Certificates & Badges", hint: "Credentials wallet shortcut with unread badge." },
];

// Lesson Plan Coverage Report section toggles, stored in
// customer_detail_master.iTeachAppConfig.lessonPlanCoverageReport. Both are
// default-on (absent key === section shown). Keys are historical/underscored.
type LcmSectionKey = "LessonPlan_Coverage" | "Attendance_Sessions";

const LCM_SECTION_DEFS: { key: LcmSectionKey; label: string; hint: string }[] = [
  {
    key: "LessonPlan_Coverage",
    label: "LessonPlan Coverage",
    hint: "LCS Planned, LCS Covered and LCS Difference columns.",
  },
  {
    key: "Attendance_Sessions",
    label: "Attendance Sessions",
    hint: "Master switch for the attendance columns below. Turning this off hides the whole group.",
  },
];

// Individual Attendance Sessions columns. Labels match the exported Excel
// headers exactly so what the tenant picks here is what they see in the file.
const ATTENDANCE_COLUMN_DEFS: { key: AttendanceColumnKey; label: string }[] = [
  { key: "p", label: "Sessions Planned" },
  { key: "totalHeld", label: "Total Held" },
  { key: "held", label: "Held (H)" },
  { key: "exam", label: "Exam (E)" },
  { key: "diffPH", label: "Diff (P-H)" },
  { key: "swapped", label: "Swapped (S)" },
  { key: "cancelled", label: "Cancelled" },
  { key: "additional", label: "Additional (A)" },
  { key: "holiday", label: "Holiday" },
];

const EMPTY_LCM: LcmConfig = {
  LessonPlan_Coverage: true,
  Attendance_Sessions: true,
  attendanceColumns: Object.fromEntries(
    ATTENDANCE_COLUMN_KEYS.map((k) => [k, true]),
  ) as Record<AttendanceColumnKey, boolean>,
};

const EMPTY_SCORM: ScormConfig = {
  appId: "",
  serviceUrl: "https://cloud.scorm.com/api/v2",
  defaultRedirectUrl: "",
  postbackUrl: "",
  hasSecret: false,
  hasPostbackSecret: false,
};

interface SlotDef {
  key: BrandingSlot;
  label: string;
  hint: string;
}

const LOGO_SLOTS: SlotDef[] = [
  { key: "logo", label: "Header Logo", hint: "Main logo shown in the iTeach/iLearn header bar" },
  { key: "iLearnPrimaryLogo", label: "iLearn Primary Logo", hint: "Logo used on the student/learner app" },
  { key: "receiptLogo", label: "Receipt / Invoice Logo", hint: "Used on printed receipts, invoices, and hall tickets" },
  { key: "logo192", label: "PWA Icon (192px)", hint: "Android home-screen icon" },
  { key: "logo512", label: "PWA Icon (512px)", hint: "High-res PWA icon for splash screens" },
  { key: "cover", label: "iLearn Cover Image", hint: "Cover/banner image on student landing" },
  { key: "desktopHeroImage", label: "Student Login Hero (iLearn)", hint: "Shown on the right side of the student login page (desktop)" },
  { key: "iteachHeroImage", label: "Admin Login Hero (iTeach)", hint: "Shown on the right side of the teacher/admin login page. If empty, the student login hero is used" },
];

const RELATED_LINKS: { label: string; to: string; tone?: string }[] = [
  { label: "Portlets & Menu", to: "/portlet-config" },
  { label: "Page-level ACL", to: "/iteach-acl-config" },
  { label: "Credential / Signing", to: "/credential-admin" },
  { label: "Payment Gateway", to: "/settings/payment-gateway" },
  { label: "Mailbox Connections", to: "/settings/mailbox" },
  { label: "Email Templates", to: "/email-templates" },
  { label: "Job Schedules", to: "/job-schedules" },
  { label: "Analytics Hub", to: "/analytics" },
  { label: "AI Usage", to: "/ai-usage-dashboard" },
  { label: "Atlas Counselor", to: "/ai-workspace-config?mode=global-atlas-counselor" },
  { label: "Atlas Inquiries", to: "/atlas-inquiries" },
  { label: "Governance Console", to: "/governance-console" },
  { label: "Error Code Help", to: "/error-help" },
];

const EMPTY: BrandingConfig = {
  logo: "",
  iLearnPrimaryLogo: "",
  receiptLogo: "",
  logo192: "",
  logo512: "",
  cover: "",
  desktopHeroImage: "",
  iteachHeroImage: "",
  logoHeight: "",
  logoWidth: "",
  boxShadow: "",
};

// Top-level keys of app-properties.json we expose as friendly form fields.
// Anything else stays editable via the Raw JSON view.
const APP_PROPS_FRIENDLY_KEYS = [
  "navbarHtml",
  "loginText1",
  "retrictLanguageTo",
  "suppressEnquiryPicker",
  "fixEnrollmentContextOnLogin",
] as const;

const stripInternal = (obj: Record<string, any>): Record<string, any> => {
  const clean: Record<string, any> = {};
  for (const k of Object.keys(obj)) {
    if (k.startsWith("_")) continue;
    clean[k] = obj[k];
  }
  return clean;
};

const TenantPreferencesPage: React.FC = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabKey>("branding");
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Branding state
  const [branding, setBranding] = useState<BrandingConfig>(EMPTY);
  const [original, setOriginal] = useState<BrandingConfig>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<BrandingSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // iLearn app-properties state
  const [appProps, setAppProps] = useState<Record<string, any>>({});
  const [appPropsOriginal, setAppPropsOriginal] = useState<Record<string, any>>({});
  const [appPropsMeta, setAppPropsMeta] = useState<{ key?: string; url?: string; exists?: boolean }>({});
  const [appPropsLoading, setAppPropsLoading] = useState(false);
  const [appPropsSaving, setAppPropsSaving] = useState(false);
  const [appPropsView, setAppPropsView] = useState<"structured" | "raw">("structured");
  const [appPropsRawDraft, setAppPropsRawDraft] = useState<string>("{}");
  const [appPropsRawError, setAppPropsRawError] = useState<string | null>(null);

  // iLearn feature flags state
  const [ilearnFeatures, setIlearnFeatures] = useState<Record<string, boolean>>({});
  const [heroButtons, setHeroButtons] = useState<Record<string, boolean>>({});
  const [ilearnFeaturesLoading, setIlearnFeaturesLoading] = useState(false);
  const [ilearnFeaturesSaving, setIlearnFeaturesSaving] = useState<string | null>(null);

  // SCORM Cloud config state
  const [scorm, setScorm] = useState<ScormConfig>(EMPTY_SCORM);
  const [scormOriginal, setScormOriginal] = useState<ScormConfig>(EMPTY_SCORM);
  const [scormSecretDraft, setScormSecretDraft] = useState("");
  const [scormPostbackSecretDraft, setScormPostbackSecretDraft] = useState("");
  const [showScormSecret, setShowScormSecret] = useState(false);
  const [showScormPostbackSecret, setShowScormPostbackSecret] = useState(false);
  const [scormLoading, setScormLoading] = useState(false);
  const [scormSaving, setScormSaving] = useState(false);
  const [scormTesting, setScormTesting] = useState(false);
  const [scormTest, setScormTest] = useState<{ ok: boolean; message: string } | null>(null);

  // LCM — Lesson Plan Coverage Report section toggles
  const [lcm, setLcm] = useState<LcmConfig>(EMPTY_LCM);
  const [lcmLoading, setLcmLoading] = useState(false);
  const [lcmSaving, setLcmSaving] = useState(false);

  // Receipt & Invoice — document templates + active invoice template
  const [receiptTemplates, setReceiptTemplates] = useState<{ Id: number; Name: string }[]>([]);
  const [receiptStarters, setReceiptStarters] = useState<{ id: string; name: string; category: string; html: string }[]>([]);
  const [receiptActiveId, setReceiptActiveId] = useState<number | null>(null);
  const [receiptOriginalId, setReceiptOriginalId] = useState<number | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptCreating, setReceiptCreating] = useState<string | null>(null);

  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // Deep-link support: /tenant-preferences?tab=scorm lands directly on a tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("tab") as TabKey | null;
    if (requested && TAB_KEYS.includes(requested)) {
      setActiveTab(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== "branding") return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "ilearn-props") return;
    void loadAppProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "scorm") return;
    void loadScorm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "ilearn-features") return;
    void loadIlearnFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "lcm") return;
    void loadLcm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "receipt") return;
    void loadReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3500);
    return () => clearTimeout(t);
  }, [success]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await TenantPreferencesService.getBranding();
      const clean: BrandingConfig = { ...EMPTY, ...data };
      setBranding(clean);
      setOriginal(clean);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load branding");
    } finally {
      setLoading(false);
    }
  };

  const dirty = JSON.stringify(branding) !== JSON.stringify(original);

  const setField = (key: keyof BrandingConfig, value: string) => {
    setBranding((b) => ({ ...b, [key]: value }));
  };

  const handlePickFile = (slot: BrandingSlot) => {
    fileInputs.current[slot]?.click();
  };

  const handleFileChosen = async (slot: BrandingSlot, file: File | undefined) => {
    if (!file) return;
    setUploadingSlot(slot);
    setError(null);
    try {
      const { url } = await TenantPreferencesService.uploadLogo(file, slot);
      setBranding((b) => ({ ...b, [slot]: url }));
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Upload failed");
    } finally {
      setUploadingSlot(null);
      // reset the input so the same file can be re-picked
      if (fileInputs.current[slot]) fileInputs.current[slot]!.value = "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data: saved, warning } = await TenantPreferencesService.saveBranding(branding);
      const clean: BrandingConfig = { ...EMPTY, ...saved };
      setBranding(clean);
      setOriginal(clean);
      if (warning) {
        setError(warning);
      } else {
        setSuccess("Branding saved. PWA icons + favicon will pick up on the next ilearn page load (no cache bust required — app-properties.json is now uploaded with no-cache).");
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => setBranding(original);

  const handleClearSlot = (slot: BrandingSlot) => {
    setBranding((b) => ({ ...b, [slot]: "" }));
  };

  // ── iLearn app-properties handlers ───────────────────────────────────────
  const loadAppProperties = async () => {
    setAppPropsLoading(true);
    setError(null);
    try {
      const data: AppPropertiesResult = await TenantPreferencesService.getAppProperties();
      const meta = { key: data._key, url: data._url, exists: data._exists };
      const clean = stripInternal(data);
      setAppProps(clean);
      setAppPropsOriginal(clean);
      setAppPropsMeta(meta);
      setAppPropsRawDraft(JSON.stringify(clean, null, 2));
      setAppPropsRawError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load app-properties");
    } finally {
      setAppPropsLoading(false);
    }
  };

  const appPropsDirty =
    appPropsView === "raw"
      ? appPropsRawDraft !== JSON.stringify(appPropsOriginal, null, 2)
      : JSON.stringify(appProps) !== JSON.stringify(appPropsOriginal);

  const setAppPropField = (key: string, value: any) => {
    setAppProps((p) => {
      const next = { ...p };
      if (value === "" || value === undefined || value === null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleSwitchAppPropsView = (next: "structured" | "raw") => {
    if (next === appPropsView) return;
    if (next === "raw") {
      // Going to raw — serialize current structured state
      setAppPropsRawDraft(JSON.stringify(appProps, null, 2));
      setAppPropsRawError(null);
    } else {
      // Going to structured — try to parse the raw draft
      try {
        const parsed = JSON.parse(appPropsRawDraft || "{}");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("Top-level value must be a JSON object");
        }
        setAppProps(parsed);
        setAppPropsRawError(null);
      } catch (e: any) {
        setAppPropsRawError(e?.message || "Invalid JSON");
        return;
      }
    }
    setAppPropsView(next);
  };

  const handleSaveAppProps = async () => {
    let payload: Record<string, any>;
    if (appPropsView === "raw") {
      try {
        payload = JSON.parse(appPropsRawDraft || "{}");
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
          throw new Error("Top-level value must be a JSON object");
        }
      } catch (e: any) {
        setAppPropsRawError(e?.message || "Invalid JSON");
        return;
      }
    } else {
      payload = appProps;
    }

    setAppPropsSaving(true);
    setError(null);
    try {
      const saved = await TenantPreferencesService.saveAppProperties(payload, "replace");
      const meta = { key: saved._key, url: saved._url, exists: saved._exists };
      const clean = stripInternal(saved);
      setAppProps(clean);
      setAppPropsOriginal(clean);
      setAppPropsMeta(meta);
      setAppPropsRawDraft(JSON.stringify(clean, null, 2));
      setAppPropsRawError(null);
      setSuccess("app-properties.json saved. The file is uploaded with no-cache, so the next ilearn page load will fetch the new version automatically.");
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Save failed");
    } finally {
      setAppPropsSaving(false);
    }
  };

  const handleCopyLink = async (to: string) => {
    const url = /^https?:\/\//i.test(to) ? to : `${window.location.origin}${to}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedLink(to);
    setTimeout(() => setCopiedLink((c) => (c === to ? null : c)), 1500);
  };

  const handleRevertAppProps = () => {
    setAppProps(appPropsOriginal);
    setAppPropsRawDraft(JSON.stringify(appPropsOriginal, null, 2));
    setAppPropsRawError(null);
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderBranding = () => (
    <>
      <div className="tp-card">
        <h3>Logos</h3>
        <p className="tp-card-desc">
          Upload PNG, JPG, or SVG. Images are auto-resized to a maximum width of 1024px (SVGs pass
          through unchanged). The receipt logo appears on invoices, hall tickets, and SOA documents.
        </p>
        <div className="tp-slot-grid">
          {LOGO_SLOTS.map((slot) => {
            const url = (branding[slot.key] || "") as string;
            const isUploading = uploadingSlot === slot.key;
            return (
              <div key={slot.key} className="tp-slot">
                <div className="tp-slot-label">{slot.label}</div>
                <div className="tp-slot-hint">{slot.hint}</div>
                <div className="tp-preview">
                  {url ? <img src={url} alt={slot.label} /> : <span className="tp-empty">No image</span>}
                </div>
                <div className="tp-url-row">
                  <input
                    type="text"
                    value={url}
                    placeholder="https://..."
                    onChange={(e) => setField(slot.key as keyof BrandingConfig, e.target.value)}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="tp-url-copy"
                    onClick={() => url && void handleCopyLink(url)}
                    disabled={!url}
                    title={copiedLink === url && url ? "Copied!" : "Copy URL"}
                    aria-label="Copy URL"
                  >
                    {copiedLink === url && url ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <input
                  ref={(el) => { fileInputs.current[slot.key] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChosen(slot.key, e.target.files?.[0])}
                />
                <div className="tp-slot-actions">
                  <button
                    type="button"
                    className="tp-btn tp-btn-primary"
                    onClick={() => handlePickFile(slot.key)}
                    disabled={isUploading}
                  >
                    <Upload size={14} />
                    {isUploading ? "Uploading…" : "Upload"}
                  </button>
                  {url && (
                    <button
                      type="button"
                      className="tp-btn tp-btn-danger-text"
                      onClick={() => handleClearSlot(slot.key)}
                      title="Clear"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="tp-card">
        <h3>Header Logo Dimensions</h3>
        <p className="tp-card-desc">
          Overrides the on-page rendering size of the header logo (CSS pixels). Leave blank to use
          the image's natural size.
        </p>
        <div className="tp-dimensions">
          <div>
            <label className="tp-field-label" htmlFor="tp-logo-w">Width (px)</label>
            <input
              id="tp-logo-w"
              className="tp-field-input"
              type="number"
              min={0}
              value={branding.logoWidth}
              onChange={(e) => setField("logoWidth", e.target.value)}
            />
          </div>
          <div>
            <label className="tp-field-label" htmlFor="tp-logo-h">Height (px)</label>
            <input
              id="tp-logo-h"
              className="tp-field-input"
              type="number"
              min={0}
              value={branding.logoHeight}
              onChange={(e) => setField("logoHeight", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="tp-toolbar">
        {dirty && <span className="tp-dirty-flag">Unsaved changes</span>}
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={handleRevert}
          disabled={!dirty || saving}
        >
          <RefreshCw size={14} /> Revert
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </>
  );

  const renderAppProperties = () => {
    const otherKeys = Object.keys(appProps).filter(
      (k) => !APP_PROPS_FRIENDLY_KEYS.includes(k as any),
    );
    return (
      <>
        <div className="tp-card">
          <div className="tp-appprops-meta">
            <div>
              <div className="tp-slot-label">S3 source</div>
              {appPropsMeta.url ? (
                <a
                  className="tp-appprops-link"
                  href={appPropsMeta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {appPropsMeta.key}
                </a>
              ) : (
                <span className="tp-slot-hint">—</span>
              )}
              <div className="tp-slot-hint" style={{ marginTop: 4 }}>
                {appPropsMeta.exists === false
                  ? "Not yet created — saving will write it for the first time."
                  : "Browsers may cache this file. If CloudFront fronts the bucket, invalidate the path after saving."}
              </div>
            </div>
            <div className="tp-appprops-toggle">
              <button
                type="button"
                className={`tp-btn ${appPropsView === "structured" ? "tp-btn-primary" : "tp-btn-secondary"}`}
                onClick={() => handleSwitchAppPropsView("structured")}
              >
                Structured
              </button>
              <button
                type="button"
                className={`tp-btn ${appPropsView === "raw" ? "tp-btn-primary" : "tp-btn-secondary"}`}
                onClick={() => handleSwitchAppPropsView("raw")}
              >
                <FileJson size={14} /> Raw JSON
              </button>
            </div>
          </div>
        </div>

        {appPropsView === "structured" && (
          <>
            <div className="tp-card">
              <h3>Navbar HTML</h3>
              <p className="tp-card-desc">
                Free-form HTML rendered in the iLearn top navbar (via{" "}
                <code>dangerouslySetInnerHTML</code>). Used for partner / co-branding logos and
                taglines on the login page. Leave blank to fall back to the default tenant logo.
              </p>
              <textarea
                className="tp-appprops-textarea"
                value={typeof appProps.navbarHtml === "string" ? appProps.navbarHtml : ""}
                onChange={(e) => setAppPropField("navbarHtml", e.target.value)}
                rows={8}
                spellCheck={false}
                placeholder="<div>…</div>"
              />
              {typeof appProps.navbarHtml === "string" && appProps.navbarHtml.trim() && (
                <div className="tp-appprops-preview">
                  <div className="tp-slot-hint" style={{ marginBottom: 6 }}>Preview</div>
                  <div
                    className="tp-appprops-preview-frame"
                    dangerouslySetInnerHTML={{ __html: appProps.navbarHtml }}
                  />
                </div>
              )}
            </div>

            <div className="tp-card">
              <h3>Login Page Text</h3>
              <p className="tp-card-desc">
                Optional message shown on the iLearn login page (<code>loginText1</code>).
              </p>
              <textarea
                className="tp-appprops-textarea"
                value={typeof appProps.loginText1 === "string" ? appProps.loginText1 : ""}
                onChange={(e) => setAppPropField("loginText1", e.target.value)}
                rows={3}
                spellCheck={false}
              />
            </div>

            <div className="tp-card">
              <h3>Locale & Flow</h3>
              <p className="tp-card-desc">
                Common iLearn behavior switches. Other keys (navbar links, kids mode, judge0,
                LRS, theme) are not yet exposed here — edit them via the Raw JSON view.
              </p>
              <div className="tp-dimensions" style={{ maxWidth: 640 }}>
                <div>
                  <label className="tp-field-label" htmlFor="tp-restrict-lang">Restrict Language To</label>
                  <input
                    id="tp-restrict-lang"
                    className="tp-field-input"
                    type="text"
                    placeholder="e.g. en, hi, ta"
                    value={typeof appProps.retrictLanguageTo === "string" ? appProps.retrictLanguageTo : ""}
                    onChange={(e) => setAppPropField("retrictLanguageTo", e.target.value)}
                  />
                  <div className="tp-slot-hint">Key: <code>retrictLanguageTo</code> (preserves existing typo)</div>
                </div>
                <div>
                  <label className="tp-field-label" htmlFor="tp-suppress-enq">Suppress Enquiry Picker</label>
                  <select
                    id="tp-suppress-enq"
                    className="tp-field-input"
                    value={appProps.suppressEnquiryPicker === true ? "true" : appProps.suppressEnquiryPicker === false ? "false" : ""}
                    onChange={(e) =>
                      setAppPropField(
                        "suppressEnquiryPicker",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">(unset)</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </div>
                <div>
                  <label className="tp-field-label" htmlFor="tp-fix-enrollment">Fix Enrollment Context On Login</label>
                  <select
                    id="tp-fix-enrollment"
                    className="tp-field-input"
                    value={appProps.fixEnrollmentContextOnLogin === true ? "true" : appProps.fixEnrollmentContextOnLogin === false ? "false" : ""}
                    onChange={(e) =>
                      setAppPropField(
                        "fixEnrollmentContextOnLogin",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">(unset)</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </div>
              </div>
            </div>

            {otherKeys.length > 0 && (
              <div className="tp-card">
                <h3>Other Keys</h3>
                <p className="tp-card-desc">
                  Keys present in the file but not exposed as friendly fields. Switch to Raw JSON
                  to edit them.
                </p>
                <ul className="tp-appprops-otherkeys">
                  {otherKeys.map((k) => (
                    <li key={k}>
                      <code>{k}</code>
                      <span className="tp-slot-hint">
                        {Array.isArray(appProps[k])
                          ? `array (${appProps[k].length})`
                          : typeof appProps[k]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {appPropsView === "raw" && (
          <div className="tp-card">
            <h3>Raw JSON</h3>
            <p className="tp-card-desc">
              The complete <code>app-properties.json</code> object as it will be written to S3.
              Top-level must be a JSON object.
            </p>
            <textarea
              className="tp-appprops-textarea tp-appprops-textarea-json"
              value={appPropsRawDraft}
              onChange={(e) => {
                setAppPropsRawDraft(e.target.value);
                if (appPropsRawError) setAppPropsRawError(null);
              }}
              rows={24}
              spellCheck={false}
            />
            {appPropsRawError && (
              <div className="tp-alert tp-alert-error" style={{ marginTop: 12 }}>
                <span>{appPropsRawError}</span>
              </div>
            )}
          </div>
        )}

        <div className="tp-toolbar">
          {appPropsDirty && <span className="tp-dirty-flag">Unsaved changes</span>}
          <button
            type="button"
            className="tp-btn tp-btn-secondary"
            onClick={handleRevertAppProps}
            disabled={!appPropsDirty || appPropsSaving}
          >
            <RefreshCw size={14} /> Revert
          </button>
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={handleSaveAppProps}
            disabled={!appPropsDirty || appPropsSaving}
          >
            {appPropsSaving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </>
    );
  };

  // ---------- SCORM Cloud ----------
  const loadScorm = async () => {
    setScormLoading(true);
    setError(null);
    setScormTest(null);
    setScormSecretDraft("");
    setScormPostbackSecretDraft("");
    try {
      const cfg = await TenantPreferencesService.getScormConfig();
      setScorm(cfg);
      setScormOriginal(cfg);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load SCORM config");
    } finally {
      setScormLoading(false);
    }
  };

  const setScormField = (key: keyof ScormConfig, value: string) => {
    setScorm((prev) => ({ ...prev, [key]: value }));
  };

  const scormDirty =
    scorm.appId !== scormOriginal.appId ||
    scorm.serviceUrl !== scormOriginal.serviceUrl ||
    scorm.defaultRedirectUrl !== scormOriginal.defaultRedirectUrl ||
    scorm.postbackUrl !== scormOriginal.postbackUrl ||
    scormSecretDraft.trim().length > 0 ||
    scormPostbackSecretDraft.trim().length > 0;

  const handleSaveScorm = async () => {
    setScormSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveScormConfig({
        appId: scorm.appId,
        serviceUrl: scorm.serviceUrl,
        defaultRedirectUrl: scorm.defaultRedirectUrl,
        postbackUrl: scorm.postbackUrl,
        ...(scormSecretDraft.trim() ? { secretKey: scormSecretDraft.trim() } : {}),
        ...(scormPostbackSecretDraft.trim() ? { postbackSecret: scormPostbackSecretDraft.trim() } : {}),
      });
      setScorm(saved);
      setScormOriginal(saved);
      setScormSecretDraft("");
      setScormPostbackSecretDraft("");
      setScormTest(null);
      setSuccess("SCORM Cloud connection saved. Credentials take effect immediately — no restart needed. Use Test Connection to verify.");
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to save SCORM config");
    } finally {
      setScormSaving(false);
    }
  };

  const handleTestScorm = async () => {
    setScormTesting(true);
    setScormTest(null);
    try {
      const res = await ScormService.testConnection();
      setScormTest({
        ok: !!res.data?.ok,
        message: res.data?.ok
          ? `Connected to SCORM Cloud (app ${res.data.appId})`
          : "SCORM Cloud did not respond to ping — check the App ID and Secret Key.",
      });
    } catch (e: any) {
      setScormTest({
        ok: false,
        message: e?.response?.data?.error?.message || e?.message || "Test failed",
      });
    } finally {
      setScormTesting(false);
    }
  };

  // ---------- iLearn Features ----------
  const loadIlearnFeatures = async () => {
    setIlearnFeaturesLoading(true);
    setError(null);
    try {
      const data = await TenantPreferencesService.getIlearnFeatures();
      setIlearnFeatures(data.features);
      setHeroButtons(data.heroButtons);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load iLearn features");
    } finally {
      setIlearnFeaturesLoading(false);
    }
  };

  // Flags are default-on: absent key === enabled. Each toggle saves immediately.
  const handleToggleIlearnFlag = async (
    group: "features" | "heroButtons",
    key: string,
    defs: { key: string; label: string }[],
  ) => {
    const current = group === "features" ? ilearnFeatures : heroButtons;
    const next = current[key] === false; // currently off → turn on
    setIlearnFeaturesSaving(`${group}:${key}`);
    setError(null);
    setSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveIlearnFeatures({
        [group]: { [key]: next },
      });
      setIlearnFeatures(saved.features);
      setHeroButtons(saved.heroButtons);
      const def = defs.find((d) => d.key === key);
      setSuccess(
        `${def?.label || key} ${next ? "enabled" : "disabled"} for students. Takes effect on their next page load.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to save feature toggle");
    } finally {
      setIlearnFeaturesSaving(null);
    }
  };

  const renderToggleRows = (
    group: "features" | "heroButtons",
    defs: { key: string; label: string; hint: string }[],
    flags: Record<string, boolean>,
  ) =>
    defs.map((def) => {
      const enabled = flags[def.key] !== false;
      const saving = ilearnFeaturesSaving === `${group}:${def.key}`;
      return (
        <div key={def.key} className="tp-feature-row">
          <div className="tp-feature-text">
            <div className="tp-feature-label">{def.label}</div>
            <div className="tp-slot-hint">{def.hint}</div>
          </div>
          <button
            type="button"
            className={`tp-switch ${enabled ? "on" : ""}`}
            role="switch"
            aria-checked={enabled}
            aria-label={`${def.label} ${enabled ? "enabled" : "disabled"}`}
            disabled={saving}
            onClick={() => void handleToggleIlearnFlag(group, def.key, defs)}
          >
            <span className="tp-switch-knob" />
          </button>
        </div>
      );
    });

  const renderIlearnFeatures = () => (
    <>
      <div className="tp-card">
        <h3>Student-Facing Features</h3>
        <p className="tp-card-desc">
          Show or hide iLearn features for this tenant. Stored in{" "}
          <code>customer_detail_master.iLearnAppConfig.features</code>; changes reach students on
          their next page load (config cache busts immediately on save).
        </p>
        {renderToggleRows("features", ILEARN_FEATURE_DEFS, ilearnFeatures)}
      </div>
      <div className="tp-card">
        <h3>Student Hub Buttons</h3>
        <p className="tp-card-desc">
          Quick-action buttons in the Student Hub hero area (
          <code>iLearnAppConfig.studentHub.heroButtons</code>). Same default-on behavior — turn a
          button off to hide it for this tenant.
        </p>
        {renderToggleRows("heroButtons", HERO_BUTTON_DEFS, heroButtons)}
      </div>
    </>
  );

  // ---------- LCM (Lesson Plan Coverage Report sections) ----------
  /**
   * Normalize a server payload into a complete LcmConfig. `attendanceColumns` is
   * merged key-by-key (not spread wholesale) so a tenant saved before per-column
   * support — or any partial map — still lands on all nine columns default-on.
   */
  const mergeLcm = (data: Partial<LcmConfig>): LcmConfig => ({
    ...EMPTY_LCM,
    ...data,
    attendanceColumns: {
      ...EMPTY_LCM.attendanceColumns,
      ...(data?.attendanceColumns || {}),
    },
  });

  const loadLcm = async () => {
    setLcmLoading(true);
    setError(null);
    try {
      const data = await TenantPreferencesService.getLcmConfig();
      setLcm(mergeLcm(data));
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load LCM config");
    } finally {
      setLcmLoading(false);
    }
  };

  // Toggling a section auto-saves immediately (optimistic; reverts on error).
  const toggleLcm = async (key: LcmSectionKey) => {
    if (lcmSaving) return;
    const prev = lcm;
    const next: LcmConfig = { ...lcm, [key]: !lcm[key] };
    setLcm(next);
    setLcmSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveLcmConfig(next);
      setLcm(mergeLcm(saved));
      setSuccess("Lesson Plan Coverage sections updated. Takes effect the next time the report is generated.");
    } catch (e: any) {
      setLcm(prev);
      setError(e?.response?.data?.error?.message || e?.message || "Failed to save LCM config");
    } finally {
      setLcmSaving(false);
    }
  };

  // Individual attendance column — same optimistic auto-save as toggleLcm.
  const toggleAttendanceColumn = async (key: AttendanceColumnKey) => {
    if (lcmSaving) return;
    const prev = lcm;
    const next: LcmConfig = {
      ...lcm,
      attendanceColumns: { ...lcm.attendanceColumns, [key]: !lcm.attendanceColumns[key] },
    };
    setLcm(next);
    setLcmSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveLcmConfig(next);
      setLcm(mergeLcm(saved));
      setSuccess("Attendance session columns updated. Takes effect the next time the report is generated.");
    } catch (e: any) {
      setLcm(prev);
      setError(e?.response?.data?.error?.message || e?.message || "Failed to save LCM config");
    } finally {
      setLcmSaving(false);
    }
  };

  const renderLcm = () => {
    const shown = LCM_SECTION_DEFS.filter((d) => lcm[d.key]).map((d) => d.label);
    const attendanceOn = lcm.Attendance_Sessions;
    const shownAttendance = attendanceOn
      ? ATTENDANCE_COLUMN_DEFS.filter((d) => lcm.attendanceColumns[d.key]).map((d) => d.label)
      : [];
    return (
      <>
        <div className="tp-card">
          <h3>Report Sections</h3>
          <p className="tp-card-desc">
            Choose which top-level column groups appear on the Lesson Plan Coverage Report.
          </p>
          {LCM_SECTION_DEFS.map((def) => {
            const enabled = lcm[def.key];
            return (
              <div key={def.key} className="tp-feature-row">
                <div className="tp-feature-text">
                  <div className="tp-feature-label">{def.label}</div>
                  <div className="tp-slot-hint">{def.hint}</div>
                </div>
                <button
                  type="button"
                  className={`tp-switch ${enabled ? "on" : ""}`}
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`${def.label} ${enabled ? "shown" : "hidden"}`}
                  disabled={lcmSaving}
                  onClick={() => toggleLcm(def.key)}
                >
                  <span className="tp-switch-knob" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="tp-card" style={attendanceOn ? undefined : { opacity: 0.55 }}>
          <h3>Attendance Session Columns</h3>
          <p className="tp-card-desc">
            {attendanceOn
              ? "Choose which attendance columns appear on the report and in the Excel export."
              : "Attendance Sessions is switched off above — the whole group is hidden regardless of these settings."}
          </p>
          {ATTENDANCE_COLUMN_DEFS.map((def) => {
            const enabled = lcm.attendanceColumns[def.key];
            return (
              <div key={def.key} className="tp-feature-row">
                <div className="tp-feature-text">
                  <div className="tp-feature-label">{def.label}</div>
                </div>
                <button
                  type="button"
                  className={`tp-switch ${enabled ? "on" : ""}`}
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`${def.label} ${enabled ? "shown" : "hidden"}`}
                  disabled={lcmSaving || !attendanceOn}
                  onClick={() => toggleAttendanceColumn(def.key)}
                >
                  <span className="tp-switch-knob" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="tp-card">
          <h3>Preview</h3>
          <p className="tp-card-desc">Columns the report and Excel export will render for this tenant.</p>
          {shown.length ? (
            <div className="tp-slot-hint" style={{ marginTop: 0, fontSize: 13, color: "var(--text-strong)" }}>
              {shown.join("  ·  ")}
            </div>
          ) : (
            <div className="tp-slot-hint" style={{ marginTop: 0, fontSize: 13, color: "var(--status-warning-text)" }}>
              Both sections are hidden — the report will show only batch rows with no metric columns.
            </div>
          )}
          {attendanceOn && (
            <div
              className="tp-slot-hint"
              style={{
                fontSize: 13,
                color: shownAttendance.length ? "var(--text-strong)" : "var(--status-warning-text)",
              }}
            >
              {shownAttendance.length
                ? `Attendance columns: ${shownAttendance.join("  ·  ")}`
                : "Every attendance column is switched off — the group will not appear."}
            </div>
          )}
        </div>
      </>
    );
  };

  // ---------- Receipt & Invoice (document templates + active invoice template) ----------
  const loadReceipt = async () => {
    setReceiptLoading(true);
    setError(null);
    try {
      const [listRes, cfg, startersRes] = await Promise.all([
        DocumentTemplateService.list(),
        TenantPreferencesService.getInvoiceConfig(),
        DocumentTemplateService.getStarters().catch(() => ({ data: { data: [] } })),
      ]);
      const list = (listRes.data?.data || []) as { Id: number; Name: string }[];
      setReceiptTemplates(list);
      setReceiptStarters((startersRes.data?.data || []) as { id: string; name: string; category: string; html: string }[]);
      setReceiptActiveId(cfg.activeTemplateId ?? null);
      setReceiptOriginalId(cfg.activeTemplateId ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to load invoice templates");
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleSaveReceipt = async () => {
    setReceiptSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await TenantPreferencesService.saveInvoiceConfig(receiptActiveId);
      setReceiptActiveId(saved.activeTemplateId ?? null);
      setReceiptOriginalId(saved.activeTemplateId ?? null);
      setSuccess(
        saved.activeTemplateId
          ? "Invoice template updated. New invoices will render with this template immediately."
          : "Invoice template cleared. Invoices will use the built-in default layout.",
      );
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to save invoice template");
    } finally {
      setReceiptSaving(false);
    }
  };

  const handleUseStarter = async (starter: { id: string; name: string; html: string }) => {
    setReceiptCreating(starter.id);
    setError(null);
    try {
      const res = await DocumentTemplateService.create({ Name: starter.name, Mustache: starter.html });
      const newId = res.data?.data?.Id;
      if (newId) history.push(`/document-templates/${newId}/edit`);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || "Failed to create template from standard");
      setReceiptCreating(null);
    }
  };

  const renderReceipt = () => {
    const dirty = receiptActiveId !== receiptOriginalId;
    return (
      <>
        <div className="tp-card">
          <h3>Active Invoice Template</h3>
          <p className="tp-card-desc">
            Choose which document template renders generated invoices. This is the template
            nodefork resolves at print time via{" "}
            <code>resource_master(InvoiceRunDetails).postboxTemplateIdForInvoiceGeneration</code>.
            Leave it as <em>Built-in default</em> to use the hardcoded layout.
          </p>
          {receiptTemplates.length === 0 ? (
            <div className="tp-slot-hint" style={{ marginBottom: 12 }}>
              No document templates exist yet. Create one in the builder first, then come back to
              set it as the invoice default.
            </div>
          ) : (
            <div className="tp-dimensions" style={{ maxWidth: 560 }}>
              <div>
                <label className="tp-field-label" htmlFor="tp-invoice-template">Invoice template</label>
                <select
                  id="tp-invoice-template"
                  className="tp-field-input"
                  value={receiptActiveId ?? ""}
                  onChange={(e) => setReceiptActiveId(e.target.value === "" ? null : Number(e.target.value))}
                >
                  <option value="">Built-in default (no custom template)</option>
                  {receiptTemplates.map((t) => (
                    <option key={t.Id} value={t.Id}>
                      {t.Name} (#{t.Id})
                    </option>
                  ))}
                </select>
                <div className="tp-slot-hint">
                  Applies to invoices generated after saving — existing PDFs are unchanged.
                </div>
              </div>
            </div>
          )}
          <div className="tp-toolbar">
            {dirty && <span className="tp-dirty-flag">Unsaved changes</span>}
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              onClick={handleSaveReceipt}
              disabled={!dirty || receiptSaving}
            >
              {receiptSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="tp-card">
          <h3>Start from a Standard Design</h3>
          <p className="tp-card-desc">
            Pick a ready-made invoice or receipt layout to start from — it opens in the visual
            drag-and-drop builder (the same one used for email) where you can change the logo,
            colours, wording and drag in merge-fields like {"{{invoiceNumber}}"} or {"{{toBePaid}}"}.
          </p>

          {receiptStarters.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 16,
                marginBottom: 18,
              }}
            >
              {receiptStarters.map((s) => {
                const creating = receiptCreating === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!!receiptCreating}
                    onClick={() => handleUseStarter(s)}
                    title={`Start from ${s.name}`}
                    style={{
                      textAlign: "left",
                      padding: 0,
                      border: "1px solid var(--border-default)",
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "var(--surface-card)",
                      cursor: receiptCreating ? "default" : "pointer",
                      opacity: receiptCreating && !creating ? 0.6 : 1,
                    }}
                  >
                    <div
                      style={{
                        height: 150,
                        overflow: "hidden",
                        background: "#fff",
                        borderBottom: "1px solid var(--border-default)",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          transform: "scale(0.34)",
                          transformOrigin: "top left",
                          width: "294%",
                          padding: 10,
                          pointerEvents: "none",
                        }}
                        dangerouslySetInnerHTML={{ __html: s.html }}
                      />
                      {creating && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(255,255,255,0.75)",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--brand-primary)",
                          }}
                        >
                          Opening…
                        </div>
                      )}
                    </div>
                    <div style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "capitalize" }}>
                        {s.category} · start &amp; edit
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="tp-slot-actions">
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={() => history.push("/document-templates")}
            >
              Manage all templates
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderScorm = () => (
    <>
      <div className="tp-card">
        <h3>SCORM Cloud Connection</h3>
        <p className="tp-card-desc">
          Credentials the SCORM Courses page uses to upload packages, launch courses, and sync
          learner progress. Get the Application ID and Secret Key from your{" "}
          <a href="https://cloud.scorm.com" target="_blank" rel="noreferrer">SCORM Cloud dashboard</a>{" "}
          under <em>Apps &amp; Registration Keys</em>.
        </p>

        <div className="tp-dimensions" style={{ maxWidth: 720 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-appid">Application ID</label>
            <input
              id="tp-scorm-appid"
              className="tp-field-input"
              type="text"
              autoComplete="off"
              placeholder="e.g. E4GE9R45Z9"
              value={scorm.appId}
              onChange={(e) => setScormField("appId", e.target.value)}
            />
            <div className="tp-slot-hint">Key: <code>appId</code></div>
          </div>

          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-secret">
              Secret Key {scorm.hasSecret && <span className="tp-slot-hint" style={{ fontWeight: 400 }}>(a secret is already set)</span>}
            </label>
            <div className="tp-secret-row">
              <input
                id="tp-scorm-secret"
                className="tp-field-input"
                type={showScormSecret ? "text" : "password"}
                autoComplete="new-password"
                placeholder={scorm.hasSecret ? "•••••••• (leave blank to keep current)" : "Enter secret key"}
                value={scormSecretDraft}
                onChange={(e) => setScormSecretDraft(e.target.value)}
              />
              <button
                type="button"
                className="tp-btn tp-btn-secondary tp-secret-toggle"
                onClick={() => setShowScormSecret((s) => !s)}
                title={showScormSecret ? "Hide" : "Show"}
                aria-label={showScormSecret ? "Hide secret" : "Show secret"}
              >
                {showScormSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="tp-slot-hint">Key: <code>secretKey</code> — never displayed; only overwritten when you type a new value.</div>
          </div>

          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-serviceurl">Service URL</label>
            <input
              id="tp-scorm-serviceurl"
              className="tp-field-input"
              type="text"
              placeholder="https://cloud.scorm.com/api/v2"
              value={scorm.serviceUrl}
              onChange={(e) => setScormField("serviceUrl", e.target.value)}
            />
            <div className="tp-slot-hint">Key: <code>serviceUrl</code> — SCORM Cloud API v2 base. Leave default unless self-hosting.</div>
          </div>

          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-redirect">Default Redirect URL</label>
            <input
              id="tp-scorm-redirect"
              className="tp-field-input"
              type="text"
              placeholder="(optional) where learners return after closing a course"
              value={scorm.defaultRedirectUrl}
              onChange={(e) => setScormField("defaultRedirectUrl", e.target.value)}
            />
            <div className="tp-slot-hint">Key: <code>defaultRedirectUrl</code> — optional.</div>
          </div>
        </div>
      </div>

      <div className="tp-card">
        <h3>Progress Postback (optional)</h3>
        <p className="tp-card-desc">
          When set, SCORM Cloud posts completion/score results to this webhook so learner progress
          syncs in near-real-time instead of only on a manual refresh.
        </p>
        <div className="tp-dimensions" style={{ maxWidth: 720 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-postback">Postback URL</label>
            <input
              id="tp-scorm-postback"
              className="tp-field-input"
              type="text"
              placeholder="https://<tenant>-node.edusquares.com/api/v1/scorm/postback"
              value={scorm.postbackUrl}
              onChange={(e) => setScormField("postbackUrl", e.target.value)}
            />
            <div className="tp-slot-hint">Key: <code>postbackUrl</code></div>
          </div>
          <div>
            <label className="tp-field-label" htmlFor="tp-scorm-postback-secret">
              Postback Secret {scorm.hasPostbackSecret && <span className="tp-slot-hint" style={{ fontWeight: 400 }}>(a secret is already set)</span>}
            </label>
            <div className="tp-secret-row">
              <input
                id="tp-scorm-postback-secret"
                className="tp-field-input"
                type={showScormPostbackSecret ? "text" : "password"}
                autoComplete="new-password"
                placeholder={scorm.hasPostbackSecret ? "•••••••• (leave blank to keep current)" : "(optional) shared secret guarding the webhook"}
                value={scormPostbackSecretDraft}
                onChange={(e) => setScormPostbackSecretDraft(e.target.value)}
              />
              <button
                type="button"
                className="tp-btn tp-btn-secondary tp-secret-toggle"
                onClick={() => setShowScormPostbackSecret((s) => !s)}
                title={showScormPostbackSecret ? "Hide" : "Show"}
                aria-label={showScormPostbackSecret ? "Hide postback secret" : "Show postback secret"}
              >
                {showScormPostbackSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="tp-slot-hint">Key: <code>postbackSecret</code> — appended as <code>?key=</code> to the webhook.</div>
          </div>
        </div>
      </div>

      {scormTest && (
        <div className={`tp-alert ${scormTest.ok ? "tp-alert-success" : "tp-alert-error"}`}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {scormTest.ok ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {scormTest.message}
          </span>
        </div>
      )}

      <div className="tp-toolbar">
        {scormDirty && <span className="tp-dirty-flag">Unsaved changes</span>}
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={handleTestScorm}
          disabled={scormTesting || scormSaving}
          title={scormDirty ? "Tests the currently saved credentials — save first to test new values" : "Test the saved SCORM Cloud connection"}
        >
          <Plug size={14} /> {scormTesting ? "Testing…" : "Test Connection"}
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={handleSaveScorm}
          disabled={!scormDirty || scormSaving}
        >
          {scormSaving ? "Saving…" : "Save Changes"}
        </button>
      </div>
      {scormDirty && (
        <p className="tp-slot-hint" style={{ marginTop: 8 }}>
          Test Connection validates the last <strong>saved</strong> credentials. Save your changes first to test new values.
        </p>
      )}
    </>
  );

  return (
    <div className="tenant-prefs">
      <aside className="tp-side">
        <h2>Tenant Preferences</h2>

        {/* Appearance — how the apps look (Branding → Theme → Start Page → Footer) */}
        <h2 className="tp-group-heading">Appearance</h2>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "branding" ? "active" : ""}`}
          onClick={() => setActiveTab("branding")}
        >
          <ImageIcon size={16} className="tp-nav-icon" /> Branding &amp; Logos
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "theme" ? "active" : ""}`}
          onClick={() => setActiveTab("theme")}
        >
          <Palette size={16} className="tp-nav-icon" /> Theme
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "start-page" ? "active" : ""}`}
          onClick={() => setActiveTab("start-page")}
        >
          <Home size={16} className="tp-nav-icon" /> Start Page
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "footer" ? "active" : ""}`}
          onClick={() => setActiveTab("footer")}
        >
          <PanelBottom size={16} className="tp-nav-icon" /> Footer
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "navigation" ? "active" : ""}`}
          onClick={() => setActiveTab("navigation")}
        >
          <NavigationIcon size={16} className="tp-nav-icon" /> Navigation
        </button>

        {/* Student App — what learners get in iLearn */}
        <div className="tp-divider" />
        <h2 className="tp-group-heading">Student App</h2>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "ilearn-features" ? "active" : ""}`}
          onClick={() => setActiveTab("ilearn-features")}
        >
          <Gamepad2 size={16} className="tp-nav-icon" /> iLearn Features
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "ilearn-props" ? "active" : ""}`}
          onClick={() => setActiveTab("ilearn-props")}
        >
          <FileJson size={16} className="tp-nav-icon" /> iLearn App Properties
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "atlas" ? "active" : ""}`}
          onClick={() => setActiveTab("atlas")}
        >
          <Compass size={16} className="tp-nav-icon" /> Atlas
        </button>

        {/* Operations — back-office: outputs first (Receipt, LCR), then integrations */}
        <div className="tp-divider" />
        <h2 className="tp-group-heading">Operations</h2>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "system" ? "active" : ""}`}
          onClick={() => setActiveTab("system")}
        >
          <Settings size={16} className="tp-nav-icon" /> System
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "receipt" ? "active" : ""}`}
          onClick={() => setActiveTab("receipt")}
        >
          <Receipt size={16} className="tp-nav-icon" /> Receipt &amp; Invoice
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "lcm" ? "active" : ""}`}
          onClick={() => setActiveTab("lcm")}
        >
          <ClipboardList size={16} className="tp-nav-icon" /> LCR
        </button>
        <button
          type="button"
          className={`tp-nav-btn ${activeTab === "scorm" ? "active" : ""}`}
          onClick={() => setActiveTab("scorm")}
        >
          <Box size={16} className="tp-nav-icon" /> SCORM Cloud
        </button>

        <div className="tp-divider" />
        <h2>Related</h2>
        {RELATED_LINKS.map((link) => {
          const justCopied = copiedLink === link.to;
          return (
            <div key={link.to} className="tp-related-link">
              <button
                type="button"
                className="tp-related-link-main"
                onClick={() => history.push(link.to)}
              >
                <span>{link.label}</span>
                <ExternalLink size={12} />
              </button>
              <button
                type="button"
                className="tp-related-link-copy"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleCopyLink(link.to);
                }}
                title={justCopied ? "Copied!" : "Copy link"}
                aria-label={justCopied ? "Copied" : `Copy link to ${link.label}`}
              >
                {justCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          );
        })}
      </aside>

      <main className="tp-main">
        <div className="tp-header">
          <div>
            <h1>
              {activeTab === "branding" && "Branding & Logos"}
              {activeTab === "ilearn-props" && "iLearn App Properties"}
              {activeTab === "start-page" && "Student Start Page"}
              {activeTab === "ilearn-features" && "iLearn Features"}
              {activeTab === "receipt" && "Receipt & Invoice"}
              {activeTab === "system" && "System"}
              {activeTab === "theme" && "Theme"}
              {activeTab === "footer" && "Footer"}
              {activeTab === "navigation" && "Navigation"}
              {activeTab === "scorm" && "SCORM Cloud"}
              {activeTab === "ai-providers" && "AI Providers"}
              {activeTab === "lcm" && "LCR · Lesson Plan Coverage"}
              {activeTab === "atlas" && "Atlas"}
            </h1>
            <p className="tp-subtitle">
              {activeTab === "branding" && "Logos and visual identity used across iTeach, iLearn, receipts, and PWA installs."}
              {activeTab === "ilearn-props" && "The app-properties.json file ilearn fetches at boot — navbar HTML, login text, locale, feature flags."}
              {activeTab === "start-page" && "The first screen students see every time they sign in to iLearn. Applies to this institution only."}
              {activeTab === "ilearn-features" && "Show or hide student-facing features like the Games Zone, per tenant."}
              {activeTab === "receipt" && "Receipt templates, invoice headers, and SOA document settings."}
              {activeTab === "system" && "Tenant timezone, default email & SMS sender, and the iLearn public URL — settings that apply to every user of this institution."}
              {activeTab === "theme" && "Primary, secondary, and accent colors used across the apps."}
              {activeTab === "footer" && "The footer students see on ilearn's public pages — link columns, social icons and copyright, or your own HTML."}
              {activeTab === "navigation" && "Links shown in the ilearn top navbar for signed-in students — e.g. a \"Courses\" link to the /Products catalogue."}
              {activeTab === "scorm" && "SCORM Cloud connection credentials for uploading, launching, and progress-syncing SCORM courses."}
              {activeTab === "ai-providers" && "Anthropic, OpenAI, and Pinecone connection health, the tenant's default model, usage, and the monthly spend cap."}
              {activeTab === "lcm" && "Choose which column groups — and which individual attendance session columns — appear on the Lesson Plan Coverage Report and its Excel export. Takes effect the next time the report is generated."}
              {activeTab === "atlas" && "Registration wiring and branding for the Atlas career-guidance app — default program, partner school, brand name and the OTP SMS template."}
            </p>
          </div>
        </div>

        {error && (
          <div className="tp-alert tp-alert-error">
            <span>{error}</span>
            <button type="button" className="tp-btn tp-btn-danger-text" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="tp-alert tp-alert-success">
            <span>{success}</span>
            <button type="button" className="tp-btn tp-btn-secondary" onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}

        {activeTab === "branding" && (loading
          ? <div className="tp-loading">Loading branding…</div>
          : renderBranding())}
        {activeTab === "ilearn-props" && (appPropsLoading
          ? <div className="tp-loading">Loading app-properties…</div>
          : renderAppProperties())}
        {activeTab === "start-page" && (
          <StartPagePicker onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "ilearn-features" && (ilearnFeaturesLoading
          ? <div className="tp-loading">Loading feature flags…</div>
          : renderIlearnFeatures())}
        {activeTab === "receipt" && (receiptLoading
          ? <div className="tp-loading">Loading invoice templates…</div>
          : renderReceipt())}
        {activeTab === "system" && (
          <SystemTab onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "theme" && (
          <ThemeTab onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "footer" && (
          <FooterTab onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "navigation" && (
          <NavigationTab onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "scorm" && (scormLoading
          ? <div className="tp-loading">Loading SCORM config…</div>
          : renderScorm())}
        {activeTab === "atlas" && (
          <AtlasTab onError={setError} onSuccess={setSuccess} />
        )}
        {activeTab === "lcm" && (lcmLoading
          ? <div className="tp-loading">Loading LCM config…</div>
          : renderLcm())}
      </main>
    </div>
  );
};

export default TenantPreferencesPage;
